/**
 * THE PAGES A SUPPORT ANSWER MAY LINK TO - one closed list, read by the assistant's prompt, by the
 * markup parser and by the renderer.
 *
 * ⚠ **It exists because the assistant kept NAMING a page and leaving the reader to find it** (owner,
 * 2026-08-10, with a screenshot: *"would love if the AI assistant could link to a page when it
 * recommends or guides to one"*). The answer in that shot was correct - *"you can create short clips
 * in the release workspace under Shorts"* - and it still ended with a person hunting through a
 * sidebar for a word they had just been told.
 *
 * ## Why a LIST rather than letting the model write a URL
 *
 * `shared/lightMarkup.ts` refuses `[text](url)` from a model on purpose, and the reason has not
 * changed: **an anchor a model wrote, inside a support conversation the customer already trusts, is
 * a phishing primitive in the one context where a bad link is most likely to be clicked.** That
 * refusal is right and this does not weaken it. What travels here is not a URL - it is a NAME from
 * this list, which the renderer turns into an in-app route. A model that invents a name produces no
 * link at all, and there is no spelling of anything in the subset that can address another origin.
 * **The vocabulary is the security boundary, and it is closed.**
 *
 * ## Why the labels are English and not `t()` keys
 *
 * `label` is read by the MODEL, exactly like `AbsentSurface.label` next door: it has to match the
 * word in `DOCS.md` and on the nav item so the assistant recognizes what it is being asked about,
 * and a translated label would stop matching the corpus. What the CUSTOMER sees is the link text the
 * model wrote, in their own language - the name here never reaches a screen.
 *
 * ## ⚠ Top-level pages only, and that is a decision rather than a first cut
 *
 * A release's Clips tab lives at `#releases/<id>/clips`, and an id is not something an assistant
 * grounded in documentation can know - it can see the customer's releases by name, but a link built
 * from a guess would land on somebody else's record or on nothing. So the honest destination for
 * *"open the release and go to Clips"* is **Releases**, with the rest of the route in words. A
 * deep link earns its place here the day something makes the id safe to resolve.
 */
export interface AppPage {
	/** The route name - what `navigateTo` takes, and what the nav item carries as `data-page`. */
	page: string;
	/** What the app and DOCS.md call it. Model-facing; never rendered to a customer. */
	label: string;
}

/**
 * ⚠ **Kept honest by `tests/appPages.test.ts` against `public/index.html`'s own nav**, not by
 * remembering to edit it. A page that exists in the shell and not here is invisible to the
 * assistant, which is the silent half - the link simply never gets offered and nobody files it.
 */
export const LINKABLE_PAGES: AppPage[] = [
	// ⚠ Every line carries its own `i18n-ignore` because the audit's escape hatch is per LINE, and a
	// single marker over the block would suppress exactly one of them - see `scripts/i18n-audit.ts`.
	// The reason is the same on all of them and is written out at the top of this file: these are read
	// by a MODEL matching them against DOCS.md and the nav, never rendered to anybody.
	{ page: "dashboard", label: "Dashboard" }, // i18n-ignore: model-facing, matches the nav item and DOCS.md
	{ page: "onboarding", label: "Get started" }, // i18n-ignore: model-facing, matches the nav item and DOCS.md
	{ page: "content", label: "Content" }, // i18n-ignore: model-facing, matches the nav item and DOCS.md
	{ page: "insights", label: "Insights" }, // i18n-ignore: model-facing, matches the nav item and DOCS.md
	{ page: "posts", label: "Posts" }, // i18n-ignore: model-facing, matches the nav item and DOCS.md
	{ page: "ads", label: "Ads" }, // i18n-ignore: model-facing, matches the nav item and DOCS.md
	{ page: "discover", label: "Discover Outlets" }, // i18n-ignore: model-facing, matches the nav item and DOCS.md
	{ page: "outlets", label: "Outlets" }, // i18n-ignore: model-facing, matches the nav item and DOCS.md
	{ page: "artists", label: "Artists" }, // i18n-ignore: model-facing, matches the nav item and DOCS.md
	{ page: "releases", label: "Releases" }, // i18n-ignore: model-facing, matches the nav item and DOCS.md
	{ page: "campaigns", label: "Campaigns" }, // i18n-ignore: model-facing, matches the nav item and DOCS.md
	{ page: "compose", label: "Compose" }, // i18n-ignore: model-facing, matches the nav item and DOCS.md
	{ page: "queue", label: "Queue" }, // i18n-ignore: model-facing, matches the nav item and DOCS.md
	{ page: "approved", label: "Approved" }, // i18n-ignore: model-facing, matches the nav item and DOCS.md
	{ page: "sent", label: "Sent" }, // i18n-ignore: model-facing, matches the nav item and DOCS.md
	{ page: "accepted", label: "Accepted" }, // i18n-ignore: model-facing, matches the nav item and DOCS.md
	{ page: "rejected", label: "Rejected" }, // i18n-ignore: model-facing, matches the nav item and DOCS.md
	{ page: "settings", label: "Settings" } // i18n-ignore: model-facing, matches the nav item and DOCS.md
];

/** Is this a page the app will actually route to? The parser's whole gate. */
export function isLinkablePage(page: string): boolean {
	return LINKABLE_PAGES.some(entry => entry.page === page);
}

/**
 * The paths the SHELL answers that are not pages in the nav: the auth screens.
 *
 * ⚠ **`/signup` and `/signin` need no server route** - the shell serves any non-API path and mounts
 * the login island when `/api/meta` reports no user - **which is exactly why they need a line HERE**
 * the moment that fallback stops being unconditional. They are the two addresses a landing-page CTA
 * and an expired session send people to, so a 404 on either is the app losing its own front door.
 */
export const SHELL_ONLY_PATHS = ["signin", "signup"];

/**
 * Does the single-page app answer this path?
 *
 * ⚠ **This exists so the server can 404 instead of serving 26KB of empty shell with a 200** - a
 * soft-404, which is the shape a search engine cannot tell from a real page and a person cannot tell
 * from a broken one. Every unknown URL used to get the app.
 *
 * ⚠ **It is a CLOSED list on purpose, and getting it wrong 404s a real page.** `LINKABLE_PAGES` is
 * already kept honest against `public/index.html`'s nav by `tests/appPages.test.ts`, so the set is
 * derived from the thing it has to agree with rather than hand-written a second time - and only the
 * FIRST segment decides, because `/releases/12/link` is the releases page with a sub-path.
 *
 * ⚠ **Through `canonicalPage`, so a renamed page's old address still lands** (`/blogs` → Outlets). A
 * name is an address; bookmarks and support answers carry the old one, and 404ing it would turn a
 * rename into a dead link - which is the failure `PAGE_ALIASES` exists to prevent, one layer up.
 */
export function servesAppShell(pathname: string): boolean {
	const first = pathname.replace(/^\/+/, "").split("/")[0] ?? "";
	// "/" is the dashboard when signed in and the sign-in screen when not - always the shell.
	if (first === "") return true;
	if (SHELL_ONLY_PATHS.includes(first)) return true;
	return isLinkablePage(canonicalPage(first));
}

/**
 * Page names that used to be something else, and what they mean now.
 *
 * ⚠ **A page name is an ADDRESS, and renaming one breaks links that already exist** - a bookmark, an
 * answer the support assistant wrote naming the page, a sentence in DOCS.md somebody followed.
 * `blogs` became `outlets` on 2026-08-20, catching up with what the nav has called it for a long
 * time; the old spelling still has to ARRIVE somewhere rather than falling through to the dashboard,
 * which is the failure that reads as the app losing the page. **The row stays forever: it costs one
 * line, and the alternative is a dead link.** Same rule as `RELEASE_TAB_ALIASES` one level down.
 */
export const PAGE_ALIASES: Record<string, string> = { blogs: "outlets" };

/** The page this name means, following any rename - the name itself when it was never renamed */
export function canonicalPage(page: string): string {
	return PAGE_ALIASES[page] ?? page;
}

/**
 * The address of a page, in one place.
 *
 * ⚠ **This is the line the History API move changed, and it was written the same day predicting it**
 * (2026-08-10). Every in-app link an AI answer produces comes through here, so the switch from
 * `#blogs` to `/blogs` was one edit rather than a search through model-facing prose nobody can grep -
 * and the PROMPT still says `#name`, because what the model writes is a name from a closed list and
 * this function is what turns a name into an address. The two were never the same thing.
 */
export function appPageHref(page: string, subPath: (string | number)[] = []): string {
	return `/${[page, ...subPath].join("/")}`;
}

/**
 * A `#page/sub` address, translated to a path - or null if it is not one.
 *
 * ⚠ **It exists because the old URLs are not all ours** (BACKLOG tier 2 item 16). `#settings` is the
 * Stripe billing portal's `return_url` and the checkout's, both stored on Stripe's side; the trial
 * mail sends people to one; `#settings/account/plans` is an address round 204 built on purpose and
 * anybody may have bookmarked. A path move that stopped answering those breaks links held by a third
 * party, which is the same rule the beacon's URL has one surface over - so the app translates on
 * arrival rather than pretending the hash never existed.
 *
 * ⚠ **It translates only a hash whose first segment is a REAL PAGE**, which is what keeps
 * `#main-content` - the skip link's target, and the shape of any in-document anchor - from being
 * mistaken for a route and redirected to a page that does not exist. Checking the shape alone was the
 * first version and it accepted that one, which is the difference between a pattern and a vocabulary:
 * the pattern says what an address could look like, the list says which ones exist.
 */
export function pathFromLegacyHash(hash: string): string | null {
	const raw = hash.replace(/^#/, "");
	if (raw === "" || !/^[a-z][a-z0-9-]*(\/[A-Za-z0-9._-]+)*$/.test(raw)) return null;
	const [head, ...rest] = raw.split("/");
	const page = canonicalPage(head ?? "");
	// ⚠ Rewritten to the CANONICAL name, so an old hash lands on the page's real address rather than
	// on a path the router will not recognize: two renames in one link is still one dead link.
	return isLinkablePage(page) ? `/${[page, ...rest].join("/")}` : null;
}

/**
 * NAVIGATION BY JOB (§AL round 3, 2026-09-23): the sidebar names seven JOBS, and every page is a tab
 * inside exactly one of them. The spec's rule 05 - *navigate by job, not by table* - and the reason it
 * was filed: eighteen flat rows, five of them stages of one email list, read as an admin panel.
 *
 * ⚠ **A page keeps its own address when it becomes a tab, and that is the whole redirect story.**
 * `/queue` is still `/queue` - it is the Queue tab of Outreach now - so every address already out there
 * (attention rows, the KPI tiles' `#sent`, Stripe's `/#settings` return URL, a bookmark, a support
 * answer's `(#accepted)`) lands on the right tab with nothing translated. A scheme that renamed the
 * pages (`/outreach/queue`) would have needed a redirect for every one of those and a test to find the
 * ones nobody listed; this one needs neither, and `tests/ui/navigation.test.ts` walks the whole list to
 * prove it.
 *
 * ⚠ **The ORDER inside a section is its tab order and the first visible page is where the job opens.**
 * Get started comes first in Today because while the guide is open it is the page somebody wants; it
 * is hidden the rest of the time, so Today opens on the Dashboard. Discover sits after Outlets because
 * saas does not have it, and a section whose default page an edition removes would open on nothing.
 *
 * ⚠ **Artists is a job of its own, not a Settings tab** (owner, 2026-09-23: *"'Artists' is hidden way too
 * far away... it took me way too long to find. The artist page is where users set up their link in bio
 * page, for example, which is one of our core features"*). Round 309 filed it under Settings because an
 * artist's PROFILE reads like configuration; but the workspace behind it holds the link-in-bio studio
 * and the press photos, which are things an artist makes and publishes. **File a page by what its
 * reader goes there to DO, not by what its first tab looks like.**
 */
export const APP_SECTIONS = [
	{ section: "today", pages: ["onboarding", "dashboard", "insights"] },
	{ section: "releases", pages: ["releases", "campaigns"] },
	{ section: "artists", pages: ["artists"] },
	{ section: "outreach", pages: ["compose", "queue", "approved", "sent", "accepted", "rejected"] },
	{ section: "outlets", pages: ["outlets", "discover"] },
	{ section: "social", pages: ["content", "posts", "ads"] },
	{ section: "settings", pages: ["settings"] }
] as const;

export type AppSection = (typeof APP_SECTIONS)[number]["section"];

/** The job a page belongs to - after any rename, so `/blogs` is still an Outlets page. */
export function sectionOf(page: string): AppSection {
	const canonical = canonicalPage(page);
	return (APP_SECTIONS.find(entry => (entry.pages as readonly string[]).includes(canonical)) ?? APP_SECTIONS[0]).section;
}
